'use client';

import * as React from 'react';

import { Button } from '@/components/atoms/Button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/primitives/popover';
import { cn } from '@/components/atoms/primitives/utils';

import { ACTIVE_PILL_CLASS } from './pill-active-class';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATE_RANGE = /^(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})$/;

export interface CustomRangePopoverProps {
  /**
   * Current `date` URL value. When it matches `YYYY-MM-DD..YYYY-MM-DD` the
   * popover hydrates its inputs from it; otherwise the inputs start empty.
   */
  value?: string;
  /** Called with `YYYY-MM-DD..YYYY-MM-DD` when the user confirms a valid range. */
  onConfirm: (range: string) => void;
  /**
   * Whether the parent considers the custom range active. Drives `aria-pressed`
   * on the trigger. The trigger is a popover disclosure (Radix sets
   * `aria-expanded`/`aria-haspopup`), so it stays a toggle button — it is
   * intentionally *outside* the preset `radiogroup` because `role="radio"`
   * does not permit `aria-expanded` (axe `aria-allowed-attr`). WEB-287.
   */
  isActive: boolean;
  /**
   * Base class list shared with the preset pills (PILL_BASE_CLASS in
   * `DiscoverDatePills.tsx`). Composed in by the parent so the popover
   * trigger and the radio pills can never visually drift apart. WEB-348.
   *
   * Required (not optional) to enforce the no-drift styling contract — an
   * unset `triggerClassName` would render an unstyled native `<button>` that
   * silently diverges from the preset pills. Callers MUST pass the shared
   * base class explicitly.
   */
  triggerClassName: string;
  /** Optional class for the trigger button. */
  className?: string;
}

interface ParsedRange {
  from: string;
  to: string;
}

function parseRange(value: string | undefined): ParsedRange {
  if (!value) return { from: '', to: '' };
  const match = ISO_DATE_RANGE.exec(value);
  if (!match) return { from: '', to: '' };
  return { from: match[1], to: match[2] };
}

function isValidRange(from: string, to: string): boolean {
  if (!ISO_DATE.test(from) || !ISO_DATE.test(to)) return false;
  // Lex compare works because both are zero-padded ISO calendar dates.
  return from <= to;
}

/**
 * Custom date-range popover paired with the "Custom..." pill in
 * `DiscoverDatePills`. Two ISO date inputs with light validation: both must
 * match `YYYY-MM-DD` and `from <= to`. On confirm, emits
 * `YYYY-MM-DD..YYYY-MM-DD` matching the schema in
 * `lib/discover/url-state.ts`.
 *
 * The popover trigger is the visible "Custom..." pill itself so a click on
 * the pill opens the popover (Radix wires the open/close + a11y semantics).
 */
export function CustomRangePopover({
  value,
  onConfirm,
  isActive,
  triggerClassName,
  className,
}: CustomRangePopoverProps): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const [from, setFrom] = React.useState<string>(() => parseRange(value).from);
  const [to, setTo] = React.useState<string>(() => parseRange(value).to);

  // Re-hydrate inputs when an external value change lands while the popover
  // is closed (e.g. someone deep-links to ?date=2026-04-01..2026-04-05 and
  // then opens the popover for the first time).
  React.useEffect(() => {
    if (open) return;
    const parsed = parseRange(value);
    setFrom(parsed.from);
    setTo(parsed.to);
  }, [value, open]);

  const valid = isValidRange(from, to);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!valid) return;
    onConfirm(`${from}..${to}`);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-pressed={isActive}
          // Active state shares ACTIVE_PILL_CLASS with the preset radios so
          // the two pill types can never drift apart (WEB-348, supersedes
          // WEB-287's amber decision — see pill-active-class.ts for the
          // skyline-vs-amber rationale). Base class list comes from the
          // parent (`triggerClassName`) so the trigger matches the preset
          // pills exactly without re-declaring 10+ utility classes here.
          className={cn(triggerClassName, isActive && ACTIVE_PILL_CLASS, className)}
          data-testid="discover-date-pill-custom"
        >
          Custom dates
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        // Lift above `--z-modal` sheets (WEB-679 mobile filter drawer).
        className="z-modal-nested w-72"
        data-testid="discover-date-pill-custom-popover"
      >
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1">
            <label
              htmlFor="discover-date-pill-custom-from"
              className="text-foreground text-sm font-medium"
            >
              From
            </label>
            <input
              id="discover-date-pill-custom-from"
              // `name` is required alongside `id` to satisfy React's form-field
              // a11y heuristic and to give the input a deterministic value when
              // it's submitted through a native form. WEB-274.
              name="from"
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              className="border-input bg-background text-foreground focus-visible:ring-ring h-9 w-full rounded-md border px-3 text-sm focus-visible:ring-2 focus-visible:outline-none"
              data-testid="discover-date-pill-custom-from"
              required
            />
          </div>
          <div className="space-y-1">
            <label
              htmlFor="discover-date-pill-custom-to"
              className="text-foreground text-sm font-medium"
            >
              To
            </label>
            <input
              id="discover-date-pill-custom-to"
              // Paired with `id` so React's form-field a11y heuristic stays
              // quiet — see the `name` comment on the From input. WEB-274.
              name="to"
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              min={ISO_DATE.test(from) ? from : undefined}
              className="border-input bg-background text-foreground focus-visible:ring-ring h-9 w-full rounded-md border px-3 text-sm focus-visible:ring-2 focus-visible:outline-none"
              data-testid="discover-date-pill-custom-to"
              required
            />
          </div>
          {!valid && (from.length > 0 || to.length > 0) && (
            <p
              className="text-destructive text-xs"
              role="alert"
              data-testid="discover-date-pill-custom-error"
            >
              Enter a valid range with From on or before To.
            </p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="text"
              size="sm"
              onClick={() => setOpen(false)}
              data-testid="discover-date-pill-custom-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="accent"
              size="sm"
              disabled={!valid}
              data-testid="discover-date-pill-custom-confirm"
            >
              Apply
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}
